import { isRosterSpareConfirmed } from '@sportninja/common/features/game/attendance/utils/rosterSpareConfirm'
import { isWaiverLocked } from '@sportninja/common/features/game/attendance/utils/waiverLock'
import { useEffect, useState } from 'react'
import type { AttendancePlayer } from '../types'
import { useAttendanceSection } from '../components/cells/AttendanceSectionContext'
import {
  CONFIRM_LOCKED_WAIVER_TOOLTIP,
  CONFIRM_TOOLTIP,
  DEMOTE_TOOLTIP,
  UNCONFIRM_TOOLTIP,
} from '../utils/statusCopy'

/**
 * CONFIRM / PROMOTE state for one attendance row: the confirmed checkbox, the
 * optimistic promote/demote toggle and the de-promote / withdraw gates.
 */
export const useSpareConfirmState = (player: AttendancePlayer) => {
  const {
    waiverEnforced,
    isSpareCandidate,
    promotedRequestById,
    onPromoteSpare,
    onDemoteSpare,
    onConfirmRosterSpare,
    isReadOnly,
  } = useAttendanceSection()
  const promotedRequestId = promotedRequestById?.[player.id]

  const availabilityRequest = player.availability_request
  const isSpareRow = !!availabilityRequest
  // Server-truth promoted state (is_promoted). The CONF checkbox is a toggle:
  // promoted → demote, not-promoted (and 'available') → promote.
  const serverPromoted = isSpareRow && !!player.is_promoted
  const isAvailable = isSpareRow && availabilityRequest?.status === 'available'

  // Optimistic override during an in-flight promote/demote (null = follow
  // server truth). Reverts on failure.
  const [pendingPromoted, setPendingPromoted] = useState<boolean | null>(null)
  // In-flight flag for the Roster Spares de-promote (which has no optimistic
  // checkbox state — the row just drops out on refetch).
  const [dePromoting, setDePromoting] = useState(false)
  // Optimistic override during an in-flight roster-spare confirm / unconfirm.
  const [pendingRosterConfirmed, setPendingRosterConfirmed] = useState<
    boolean | null
  >(null)
  const inFlight =
    pendingPromoted !== null || dePromoting || pendingRosterConfirmed !== null
  const serverRosterConfirmed = !isSpareRow && isRosterSpareConfirmed(player)

  // Hold the optimistic value until the refetched player actually reflects it,
  // then release — avoids a one-frame back-and-forth to stale server truth.
  useEffect(() => {
    if (pendingPromoted !== null && serverPromoted === pendingPromoted) {
      setPendingPromoted(null)
    }
  }, [pendingPromoted, serverPromoted])
  useEffect(() => {
    if (
      pendingRosterConfirmed !== null &&
      serverRosterConfirmed === pendingRosterConfirmed
    ) {
      setPendingRosterConfirmed(null)
    }
  }, [pendingRosterConfirmed, serverRosterConfirmed])

  // Roster Spares row that is a promoted spare candidate: CONFIRM is pressable
  // and de-promotes.
  const canDePromote = !isSpareCandidate && !!promotedRequestId
  // Any other roster spare is confirmed / unconfirmed by a team official — only
  // in a section that hands down the handler (Roster Spares).
  const canToggleRosterConfirm =
    !isSpareRow && !canDePromote && !!onConfirmRosterSpare
  const waiverLocked = isWaiverLocked(player, waiverEnforced, isSpareCandidate)

  const confirmed = isSpareRow
    ? pendingPromoted ?? serverPromoted
    : // A promoted spare candidate stays checked so it can be de-promoted, even
      // on an unsigned waiver (removing a player needs none). Any other roster
      // spare follows its confirm state — but an unsigned-waiver player can't
      // be confirmed, so the box is forced off for them.
      canDePromote ||
      (!waiverLocked && (pendingRosterConfirmed ?? serverRosterConfirmed))
  // Spare rows toggle promote/demote (disabled while a call is in flight, and
  // promote requires an 'available' request). Promoted-spare-candidate roster
  // spares de-promote; other roster spares toggle confirm unless waiver-locked.
  // A read-only viewer sees the box's state but can't press it.
  const confirmDisabled =
    isReadOnly ||
    (isSpareRow
      ? inFlight || (!serverPromoted && !isAvailable)
      : canDePromote
      ? inFlight
      : !canToggleRosterConfirm || waiverLocked || inFlight)
  // What pressing the CONFIRM ATTENDING box does (or why it can't). Empty when
  // it's display-only, or mid-request.
  const confirmTooltip = isReadOnly
    ? ''
    : canDePromote
    ? confirmDisabled
      ? ''
      : DEMOTE_TOOLTIP
    : waiverLocked && canToggleRosterConfirm
    ? CONFIRM_LOCKED_WAIVER_TOOLTIP
    : canToggleRosterConfirm && !inFlight
    ? confirmed
      ? UNCONFIRM_TOOLTIP
      : CONFIRM_TOOLTIP
    : ''
  const canPromote = isSpareRow && !confirmed && isAvailable
  // Any non-promoted spare row can be withdrawn, regardless of request status.
  const canWithdraw = isSpareRow && !confirmed

  const onToggleRosterConfirm = () => {
    if (
      !canToggleRosterConfirm ||
      !onConfirmRosterSpare ||
      waiverLocked ||
      inFlight
    ) {
      return
    }
    const next = !confirmed
    setPendingRosterConfirmed(next)
    // Same contract as promote: revert on failure, hold until the refetch
    // reflects it on success.
    onConfirmRosterSpare(player.id, next).then((ok) => {
      if (!ok) {
        setPendingRosterConfirmed(null)
      }
    })
  }

  const onToggleConfirm = () => {
    if (!isSpareRow) {
      onToggleRosterConfirm()
      return
    }
    if (inFlight) {
      return
    }
    const next = !confirmed
    const action = next ? onPromoteSpare : onDemoteSpare
    // Promote requires an available request; nothing to do without a handler.
    if (!action || (next && !isAvailable) || !availabilityRequest) {
      return
    }
    setPendingPromoted(next)
    // Failure: revert to server truth. Success: keep the optimistic value — the
    // effect drops it once the refetched is_promoted matches.
    action(availabilityRequest.id).then((ok) => {
      if (!ok) {
        setPendingPromoted(null)
      }
    })
  }

  const onDePromote = () => {
    if (!canDePromote || inFlight || !onDemoteSpare || !promotedRequestId) {
      return
    }
    setDePromoting(true)
    onDemoteSpare(promotedRequestId).finally(() => setDePromoting(false))
  }

  return {
    confirmed,
    confirmDisabled,
    canDePromote,
    confirmTooltip,
    canPromote,
    canWithdraw,
    inFlight,
    onToggleConfirm,
    onDePromote,
  }
}
